import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  Clock, 
  CheckCircle,
  Circle,
  BookOpen,
  Volume2,
  Settings,
  Monitor,
  Flag,
  User,
  AlertCircle,
  X
} from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLessonContext } from '../../contexts/LessonContext';
import { useTrackingContext } from '../../contexts/TrackingContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { lessonService } from '../../services/lessonService';
import { scheduleService } from '../../services/scheduleService';
import { ResourcesPanel } from './ResourcesPanel';
import { AssessmentPanel } from '../Assessments/AssessmentPanel';
import { AssessmentButton } from '../Assessments/AssessmentButton';
import { DifferentiationDisplay } from '../Lessons/DifferentiationDisplay';
import { TrackingDashboard } from '../Tracking/TrackingDashboard';
import { StudentSelector } from '../StudentSelector/StudentSelector';
import { studentService } from '../../services/studentService';
import { useAuth } from '../../contexts/AuthContext';
import { LaTeXRenderer } from '../Common/LaTeXRenderer';
import { ExportDropdown } from '../Lessons/ExportDropdown';

const StepIndicator: React.FC<{
  steps: any[];
  currentStep: number;
  onStepClick: (index: number) => void;
}> = ({ steps, currentStep, onStepClick }) => (
  <div className="flex items-center space-x-2 overflow-x-auto pb-2">
    {steps.map((step, index) => (
      <button
        key={step.id || `step-${index}`}
        onClick={() => onStepClick(index)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all min-w-0 ${
          index === currentStep
            ? 'bg-primary-100 text-primary-700 border border-primary-300'
            : index < currentStep
            ? 'bg-green-100 text-green-700 border border-green-300'
            : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
        }`}
      >
        {index < currentStep ? (
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
        ) : (
          <Circle className="w-4 h-4 flex-shrink-0" />
        )}
        <span className="text-sm font-medium truncate">{step.name || step.title || `Étape ${index + 1}`}</span>
      </button>
    ))}
  </div>
);

export const LessonSession: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useLessonContext();
  const { startTracking, stopTracking, trackAssessmentComplete, trackStepComplete } = useTrackingContext();
  const { t } = useTranslation();
  
  // Get schedule instance ID from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const scheduleInstanceId = urlParams.get('scheduleInstanceId');
  
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [stepTimer, setStepTimer] = useState(0);
  const [showResources, setShowResources] = useState(true);
  const [currentAssessment, setCurrentAssessment] = useState<{
    id: string;
    assessment: any;
  } | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(() => {
    // Load selected student from localStorage on component mount
    const saved = localStorage.getItem(`selectedStudent_${lessonId}`);
    return saved || null;
  });
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [students, setStudents] = useState<Map<string, any>>(new Map());
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [scheduleInstance, setScheduleInstance] = useState<any>(null);
  const { user } = useAuth();

  // Save selected student to localStorage
  const saveSelectedStudent = (studentId: string | null) => {
    if (lessonId) {
      if (studentId) {
        localStorage.setItem(`selectedStudent_${lessonId}`, studentId);
      } else {
        localStorage.removeItem(`selectedStudent_${lessonId}`);
      }
    }
  };

  // Load schedule instance data
  useEffect(() => {
    const loadScheduleInstance = async () => {
      if (!scheduleInstanceId) return;
      
      try {
        // For now, we'll filter students by lesson grade instead of schedule class
        // This can be enhanced later when schedule class filtering is implemented
        setScheduleInstance({ className: null }); // Placeholder
      } catch (error) {
        console.error('Error loading schedule instance:', error);
      }
    };
    
    loadScheduleInstance();
  }, [scheduleInstanceId]);

  // Load students on component mount
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoadingStudents(true);
        const fetchedStudents = await studentService.getStudents();
        const studentMap = new Map();
        fetchedStudents.forEach(student => {
          studentMap.set(student.id, student);
        });
        setStudents(studentMap);
      } catch (error) {
        console.error('Error loading students:', error);
      } finally {
        setLoadingStudents(false);
      }
    };
    loadStudents();
  }, []);

  // Reset session state when lessonId changes
  useEffect(() => {
    setCurrentStepIndex(0);
    setIsPlaying(true);
    setTimeElapsed(0);
    setStepTimer(0);
    setShowResources(false);
    
    // Load selected student for the new lesson
    if (lessonId) {
      const saved = localStorage.getItem(`selectedStudent_${lessonId}`);
      setSelectedStudentId(saved || null);
    } else {
      setSelectedStudentId(null);
    }

    // Cleanup function to clear old lesson's selected student
    return () => {
      // This will run when the component unmounts or lessonId changes
      // We don't clear localStorage here as we want to persist the selection
    };
  }, [lessonId]);

  // Find the lesson by lessonId, prioritizing currentLesson if it matches
  const lesson = state.currentLesson?.metadata.lesson_id === lessonId 
    ? state.currentLesson 
    : state.lessons.find(l => l.metadata.lesson_id === lessonId);

  useEffect(() => {
    if (!lesson && lessonId) {
      const fetchLesson = async () => {
        try {
          const fetchedLesson = await lessonService.getLessonById(lessonId);
          if (fetchedLesson) {
            dispatch({ type: 'SET_CURRENT_LESSON', payload: fetchedLesson });
          } else {
            navigate('/lessons');
          }
        } catch (error) {
          console.error('Error fetching lesson:', error);
          navigate('/lessons');
        }
      };
      fetchLesson();
    }
  }, [lessonId, lesson, dispatch, navigate]);

  // Initialize tracking when lesson is loaded and student is selected
  useEffect(() => {
    if (lesson && lessonId && selectedStudentId) {
      startTracking(lessonId, selectedStudentId, lesson.data_and_reporting);
    }

    // Cleanup tracking when component unmounts
    return () => {
      if (lessonId) {
        stopTracking();
      }
    };
  }, [lesson, lessonId, selectedStudentId, startTracking, stopTracking]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPlaying) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
        setStepTimer(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying]);

  const handleStepChange = (index: number) => {
    if (index >= 0 && index < (lesson?.timeline.length || 0)) {
      trackStepComplete(lesson?.timeline[currentStepIndex].id || '', stepTimer, false);
      setCurrentStepIndex(index);
      setStepTimer(0);
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (currentStepIndex < (lesson?.timeline.length || 0) - 1) {
      handleStepChange(currentStepIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      handleStepChange(currentStepIndex - 1);
    }
  };

  const handleFinish = async () => {
    // Stop the session
    setIsPlaying(false);

    trackStepComplete(lesson?.timeline[currentStepIndex].id || '', timeElapsed, true);
    
    // Update schedule instance status to completed if it exists
    if (scheduleInstanceId) {
      try {
        await scheduleService.updateScheduleInstance(scheduleInstanceId, { status: 'completed' });
        console.log('Schedule instance status updated to completed');
      } catch (error) {
        console.error('Error updating schedule instance status:', error);
      }
    }
    
    // Show completion message
    alert(t('lessonSession.lessonCompleted'));
    
    // Navigate back to lessons list
    navigate('/lessons');
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartAssessment = (assessmentId: string, assessment: any) => {
    setCurrentAssessment({ id: assessmentId, assessment });
  };

  const handleAssessmentComplete = (results: any) => {
    console.log('Assessment completed:', results);
    
    // Track assessment completion
    const score = results.score || 0;
    const timeSpent = results.timeSpent || 0;
    
    trackAssessmentComplete(
        results.assessmentId,
        score,
        timeSpent
    ).catch(error => console.error('Error tracking assessment completion:', error));
    
    setCurrentAssessment(null);
    // Here you could save results to Firebase or local storage
  };

  const handleAssessmentClose = () => {
    setCurrentAssessment(null);
  };

  if (!lesson) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('lessonSession.loadingLesson')}</p>
        </div>
      </div>
    );
  }

  const currentStep = lesson.timeline?.[currentStepIndex];
  const progress = lesson.timeline ? ((currentStepIndex + 1) / lesson.timeline.length) * 100 : 0;

  // Don't render if lesson or currentStep is not available
  if (!lesson || !currentStep) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={`${showResources ? 'mr-0 md:mr-96 lg:mr-96' : ''} max-w-6xl mx-auto space-y-6`}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {/* Session Header */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-4 lg:space-y-0">
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{lesson.metadata.title}</h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {lesson.metadata.subject} • {t('lesson.grade')} {lesson.metadata.grade}
                  {lesson.metadata.subconstruct && ` • ${lesson.metadata.subconstruct}`}
                  {lesson.className && ` • ${lesson.className}`}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Student Selector */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowStudentSelector(!showStudentSelector)}
                disabled={loadingStudents}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
                  selectedStudentId 
                    ? 'bg-primary-50 border-primary-300 text-primary-700' 
                    : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                } ${loadingStudents ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <User className="w-4 h-4" />
                <div className="text-left">
                  <span className="text-sm font-medium block">
                    {loadingStudents 
                      ? 'Loading...'
                      : selectedStudentId 
                        ? students.get(selectedStudentId)?.name || t('lessonSession.studentSelected')
                        : t('lessonSession.selectStudent')
                    }
                  </span>
                  {selectedStudentId && students.get(selectedStudentId) && !loadingStudents && (
                    <span className="text-xs text-gray-500 block">
                      {students.get(selectedStudentId).grade && `${students.get(selectedStudentId).grade}`}
                      {students.get(selectedStudentId).grade && students.get(selectedStudentId).class && ' • '}
                      {students.get(selectedStudentId).class && `${students.get(selectedStudentId).class}`}
                    </span>
                  )}
                </div>
              </button>
              {selectedStudentId && (
                <button
                  onClick={() => {
                    setSelectedStudentId(null);
                    saveSelectedStudent(null);
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('lessonSession.clearStudentSelection')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <div className="flex items-center justify-between sm:justify-start space-x-4 bg-gray-50 rounded-lg p-3 sm:bg-transparent sm:p-0">
              <div className="text-center sm:text-right flex-1 sm:flex-none">
                <p className="text-xs sm:text-sm text-gray-500">{t('session.totalTime')}</p>
                <p className="text-lg sm:text-lg font-bold text-gray-900">{formatTime(timeElapsed)}</p>
              </div>
              <div className="text-center sm:text-right flex-1 sm:flex-none">
                <div className="text-xs sm:text-sm text-gray-500">{t('session.stepTime')}</div>
                <p className="text-lg sm:text-lg font-bold text-gray-900">{formatTime(stepTimer)}</p>
              </div>
            </div>
            {lesson.assessments && Object.keys(lesson.assessments).length > 0 && (
              <div className="w-full sm:w-auto">
                <AssessmentButton
                  assessments={lesson.assessments}
                  onStartAssessment={handleStartAssessment}
                />
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <motion.div
            className="bg-primary-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Step Indicators */}
        <StepIndicator
          steps={lesson.timeline}
          currentStep={currentStepIndex}
          onStepClick={handleStepChange}
        />
      </div>

      {/* Student Selector Modal */}
      <AnimatePresence>
        {showStudentSelector && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="card"
          >
            <StudentSelector
              selectedStudentId={selectedStudentId}
              onStudentSelect={(studentId) => {
                setSelectedStudentId(studentId);
                saveSelectedStudent(studentId);
                setShowStudentSelector(false);
                // Refresh students list to get updated data
                const refreshStudents = async () => {
                  try {
                    const fetchedStudents = await studentService.getStudents();
                    const studentMap = new Map();
                    fetchedStudents.forEach(student => {
                      studentMap.set(student.id, student);
                    });
                    setStudents(studentMap);
                  } catch (error) {
                    console.error('Error refreshing students:', error);
                  }
                };
                refreshStudents();
              }}
              teacherId={user?.id}
              lessonFilter={{
                grade: lesson?.metadata.grade,
                className: scheduleInstance?.className || undefined
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Warning when tracking is enabled but no student is selected */}
      {lesson.data_and_reporting?.analytics_enabled && !selectedStudentId && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-lg p-4"
        >
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Tracking is enabled but no student is selected
              </p>
              <p className="text-sm text-amber-700">
                Please select a student to start tracking their progress and engagement.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Lesson Content */}
        <div className="lg:col-span-2">
          <div className="card">
            <motion.div
              key={currentStepIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Step Header */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{currentStep.name}</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-4">{currentStep.step_type}</p>
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-gray-500">
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      {currentStep.minutes} minutes
                    </span>
                    <span className="flex items-center">
                      <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      {currentStep.step_type}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!showResources && <button 
                    onClick={() => setShowResources(!showResources)}
                    className={`px-2 sm:px-3 py-2 rounded-lg transition-colors border flex items-center space-x-1 sm:space-x-2 ${
                      showResources ? 'bg-blue-100 text-blue-600 border-blue-300' : 'hover:bg-gray-100 text-gray-600 border-gray-300'
                    }`}
                    title={showResources ? t('lessonSession.hideResources') : t('lessonSession.showResources')}
                  >
                    <Monitor className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium">
                      {t('lessonSession.resources')}
                    </span>
                  </button>}
                  <button className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-300">
                    <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                  <button className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-300" onClick={() => {
                    navigate(`/lessons/${lessonId}/edit`);
                  }}>
                    <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                  <div className="ml-4 flex items-center">
                    <ExportDropdown
                      lesson={lesson}
                      size="sm"
                      variant="outline"
                    />
                  </div>
                </div>
              </div>

              {/* Teacher Script */}
              {currentStep.teacher_script && currentStep.teacher_script.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">{t('lessonSession.teacherScript')}:</h3>
                  <ol className="space-y-2">
                    {currentStep.teacher_script.map((script, index) => (
                      <li key={index} className="text-blue-800 flex items-start">
                        <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 teacher-script-content">
                          <LaTeXRenderer content={script} />
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Student Actions */}
              {currentStep.student_actions && currentStep.student_actions.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">{t('lessonSession.studentActions')}:</h3>
                  <ul className="space-y-1">
                    {currentStep.student_actions.map((action, index) => (
                      <li key={index} className="text-green-800 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Checks for Understanding */}
              {currentStep.checks_for_understanding && currentStep.checks_for_understanding.length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-900 mb-2">{t('lessonSession.checksForUnderstanding')}:</h3>
                  <ul className="space-y-2">
                    {currentStep.checks_for_understanding.map((check, index) => (
                      <li key={index} className="text-yellow-800">
                        <div className="font-medium">{check.type}:</div>
                        {check.prompt && <div className="ml-4">{check.prompt}</div>}
                        {check.look_for && <div className="ml-4 text-sm text-yellow-700">{t('lessonSession.lookFor')}: {check.look_for}</div>}
                        {check.common_error && <div className="ml-4 text-sm text-red-600">{t('lessonSession.commonError')}: {check.common_error}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Differentiation Strategies */}
              {lesson.differentiation && (
                <DifferentiationDisplay differentiation={lesson.differentiation} />
              )}
            </motion.div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="space-y-6 lg:col-span-1">
          {/* Controls */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('lessonSession.sessionControls')}</h3>
            <div className="flex items-center justify-center space-x-4 mb-4">
              <button
                onClick={handlePrevious}
                disabled={currentStepIndex === 0}
                className="p-3 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SkipBack className="w-6 h-6 text-gray-600" />
              </button>
              
              <button
                onClick={handlePlayPause}
                className="p-4 rounded-full bg-primary-600 hover:bg-primary-700 text-white transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8" />
                ) : (
                  <Play className="w-8 h-8" />
                )}
              </button>
              
              {currentStepIndex === lesson.timeline.length - 1 ? (
                <button
                  onClick={handleFinish}
                  className="p-3 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center space-x-2"
                >
                  <Flag className="w-6 h-6" />
                  <span className="text-sm font-medium">{t('lessonSession.finish')}</span>
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="p-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SkipForward className="w-6 h-6 text-gray-600" />
                </button>
              )}
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-500">{t('lessonSession.step')} {currentStepIndex + 1} {t('lessonSession.of')} {lesson.timeline.length}</p>
              <p className="text-lg font-semibold text-gray-900">{currentStep.name}</p>
            </div>
          </div>

          {/* Lesson Info */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('lessonSession.lessonInformation')}</h3>
            <div className="space-y-3">
              {lesson.metadata?.duration_minutes && (
                <div>
                  <p className="text-sm text-gray-500">{t('lessonSession.duration')}</p>
                  <p className="font-medium">{lesson.metadata.duration_minutes} {t('lessonSession.minutes')}</p>
                </div>
              )}
              {lesson.objectives && lesson.objectives.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">{t('lessonSession.objectives')}</p>
                  <ul className="mt-1 space-y-1">
                    {lesson.objectives.map((objective, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start">
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                        {objective}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lesson.materials?.digital && lesson.materials.digital.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">{t('lessonSession.digitalMaterials')}</p>
                  <ul className="mt-1 space-y-1">
                    {lesson.materials.digital.map((material, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                        {material}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lesson.materials?.physical_alternatives && lesson.materials.physical_alternatives.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">{t('lessonSession.physicalMaterials')}</p>
                  <ul className="mt-1 space-y-1">
                    {lesson.materials.physical_alternatives.map((material, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                        {material}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lesson.assessments && Object.keys(lesson.assessments).length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">{t('lessonSession.availableAssessments')}</p>
                  <div className="mt-1 space-y-1">
                    {Object.entries(lesson.assessments)
                      .filter(([id, assessment]) => assessment && assessment.items)
                      .map(([id, assessment]) => (
                        <div key={id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                          <span className="text-sm text-gray-700 font-medium">
                            {id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded">
                            {assessment.items?.length || 0} {t('lessonSession.questions')}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tracking Dashboard */}
          {lesson.data_and_reporting && (
            <TrackingDashboard />
          )}
        </div>
      </div>

      {/* Resources Panel */}
      {lesson && (
        <ResourcesPanel
          resources={lesson.resources}
          currentStep={currentStep}
          onResourceAction={(resourceId, action, data) => {
            // Handle resource actions here
            if (action === 'close' && data === 'mobile_close') {
              setShowResources(false);
            }
          }}
          isVisible={showResources}
        />
      )}

      {/* Assessment Panel */}
      {currentAssessment && (
        <AssessmentPanel
          assessment={currentAssessment.assessment}
          assessmentId={currentAssessment.id}
          resources={lesson?.resources}
          onComplete={handleAssessmentComplete}
          onClose={handleAssessmentClose}
        />
      )}
    </motion.div>
  );
};
